'use client';

import { useRouter } from '@bprogress/next/app';
import { usePathname, useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useRef, useState, useTransition } from 'react';

import { UiDoctorLayoutPage } from '@/fo/ui/doctor/layout/page';
import { UiKitDrawerBase } from '@/fo/ui/kit/drawer/base';

import { getDoctors } from '../api/getDoctors';
import { AddSpecialistForm } from '../components/AddSpecialistForm/AddSpecialistForm';
import { DoctorData, FeatureDoctorPageListProps } from '../types';

const LISTING_PAGE_PARAM = 'p';

export function FeatureDoctorPageList({
  data: initialData,
  breadcrumbs,
  banner,
  bothers = [],
  cities = [],
  occupations = [],
  qualifications = [],
  initialFilters = [],
  initialCities = [],
  initialSearchValue = '',
  initialTab = 'list',
  initialZoom
}: FeatureDoctorPageListProps) {
  const router = useRouter();
  const pathname = usePathname();
  const [data, setData] = useState<DoctorData>(initialData);
  const [mapDoctors, setMapDoctors] = useState(initialData.doctors);
  const [searchValue, setSearchValue] = useState(initialSearchValue);
  const [selectedFilters, setSelectedFilters] =
    useState<string[]>(initialFilters);
  const [isPending, startTransition] = useTransition();
  const searchParams = useSearchParams();
  const t = useTranslations();

  const [isAddSpecialistOpen, setIsAddSpecialistOpen] = useState(false);

  const validInitialCities = initialCities.filter((id) =>
    cities.some((c) => c.id === id)
  );

  const [selectedCities, setSelectedCities] = useState<string[]>(() => {
    if (validInitialCities.length > 0) return validInitialCities;
    if (initialData.city && cities.some((c) => c.id === initialData.city)) {
      return [initialData.city];
    }
    return [];
  });

  const searchValueRef = useRef(searchValue);
  const selectedFiltersRef = useRef(selectedFilters);
  const selectedCitiesRef = useRef(selectedCities);

  const filtersKey = initialFilters.join(',');
  const initialCitiesKey = initialCities.join(',');

  const navigateListing = useCallback(
    (updates: { filters?: string[]; cityIds?: string[]; query?: string }) => {
      const params = new URLSearchParams(window.location.search);

      const filters = updates.filters ?? selectedFiltersRef.current;
      if (filters.length > 0) {
        params.set('filters', filters.join(','));
      } else {
        params.delete('filters');
      }

      const cityIds = updates.cityIds ?? selectedCitiesRef.current;
      if (cityIds.length > 0) {
        params.set('city', cityIds.join(','));
      } else {
        params.delete('city');
      }

      const q =
        updates.query !== undefined
          ? updates.query.trim()
          : searchValueRef.current.trim();
      if (q) {
        params.set('query', q);
      } else {
        params.delete('query');
      }

      params.delete('p');

      const qs = params.toString();
      router.push(qs ? `${pathname}?${qs}` : pathname, { scroll: false });
    },
    [pathname, router]
  );

  useEffect(() => {
    searchValueRef.current = searchValue;
  }, [searchValue]);

  useEffect(() => {
    selectedFiltersRef.current = selectedFilters;
  }, [selectedFilters]);

  useEffect(() => {
    selectedCitiesRef.current = selectedCities;
  }, [selectedCities]);

  useEffect(() => {
    setData(initialData);
    setMapDoctors(initialData.doctors);
  }, [initialData]);

  useEffect(() => {
    setSearchValue(initialSearchValue);
    setSelectedFilters(filtersKey ? filtersKey.split(',').filter(Boolean) : []);

    const fromUrl = initialCitiesKey
      ? initialCitiesKey.split(',').filter(Boolean)
      : [];
    const matched = fromUrl.filter((id) => cities.some((c) => c.id === id));

    if (matched.length > 0) {
      setSelectedCities(matched);
    } else if (
      initialData.city &&
      cities.some((c) => c.id === initialData.city)
    ) {
      setSelectedCities([initialData.city]);
    } else {
      setSelectedCities([]);
    }
  }, [
    initialSearchValue,
    filtersKey,
    initialCitiesKey,
    initialData.city,
    cities
  ]);

  useEffect(() => {
    const addSpecialist = searchParams.get('add_specialist');
    setIsAddSpecialistOpen(addSpecialist === 'Y');
  }, [searchParams]);

  const handleSearchChange = useCallback(
    (value: string) => {
      searchValueRef.current = value;
      setSearchValue(value);
      navigateListing({ query: value });
    },
    [navigateListing]
  );

  const handleFilterChange = useCallback(
    (newFilters: string[]) => {
      selectedFiltersRef.current = newFilters;
      setSelectedFilters(newFilters);
      navigateListing({ filters: newFilters });
    },
    [navigateListing]
  );

  const handleCityChange = useCallback(
    (newCities: string[]) => {
      selectedCitiesRef.current = newCities;
      setSelectedCities(newCities);
      navigateListing({ cityIds: newCities });
    },
    [navigateListing]
  );

  const handlePageChange = useCallback(
    (page: number) => {
      const params = new URLSearchParams(window.location.search);
      const nextPage = page >= 1 ? page : 1;

      if (nextPage <= 1) {
        params.delete(LISTING_PAGE_PARAM);
      } else {
        params.set(LISTING_PAGE_PARAM, String(nextPage));
      }

      const qs = params.toString();
      router.push(qs ? `${pathname}?${qs}` : pathname, { scroll: false });
    },
    [pathname, router]
  );

  const handleAddSpecialistClose = (open: boolean) => {
    setIsAddSpecialistOpen(open);
  };

  const onAddSpecialistClose = () => {
    setIsAddSpecialistOpen(false);
  };

  const handleTabChange = (tab: 'list' | 'map') => {
    if (tab !== 'map') return;

    const maxMapDoctors = Math.min(data.pagination.totalCount, 100);

    if (mapDoctors.length >= maxMapDoctors) return;

    startTransition(async () => {
      const response = await getDoctors({
        currentPage: 1,
        searchValue,
        selectedFilters,
        cities: selectedCities,
        pageSize: 100
      });

      if (!response.data) return;

      setMapDoctors(response.data.doctors);
    });
  };

  return (
    <>
      <UiDoctorLayoutPage
        doctors={data.doctors}
        mapDoctors={mapDoctors}
        city={data.city}
        breadcrumbs={breadcrumbs}
        banner={banner}
        pagination={data.pagination}
        searchValue={searchValue}
        selectedFilters={selectedFilters}
        onSearchChange={handleSearchChange}
        onFilterChange={handleFilterChange}
        onPageChange={handlePageChange}
        cities={cities}
        selectedCities={selectedCities}
        onCityChange={handleCityChange}
        loading={isPending}
        bothers={bothers}
        openedTab={initialTab}
        initialZoom={initialZoom}
        onTabChange={handleTabChange}
      />

      <UiKitDrawerBase
        title={t('doctor.form.title')}
        open={isAddSpecialistOpen}
        onOpenChange={handleAddSpecialistClose}
        direction="right"
        withoutMobile
        handleOnly
      >
        <AddSpecialistForm
          onReset={onAddSpecialistClose}
          occupations={occupations}
          qualifications={qualifications}
        />
      </UiKitDrawerBase>
    </>
  );
}
